import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, TableCell, TableRow, Typography,
} from '@tempmee/react';
import { RouteLink } from '../../../commonComponents/RouteLink';
import { CaptionedLabel, TableCardFrame } from '../../../commonComponents/TableCardFrame';

const COLUMNS = ['Name', 'Address', 'Access'];

// The tab's only card, so its title is the tab's level-1 heading, as Account
// Info's is.
const CARD_TITLE = (
  <Typography variant="h6" as="h1">
    Locations
  </Typography>
);

/**
 * The locations a Location User can access, one row per location, in
 * organization-service's order (by name). Rows arrive mapped; nothing here
 * reformats.
 *
 * A location the monolith knows links to its office profile; one created
 * outside the monolith has no profile, so its name is plain text.
 */
export const LocationsTable = ({
  locations,
  isPending,
  isError,
  hasSearch,
  onRetry,
  toolbar,
  footer,
}) => (
  <TableCardFrame
    title={CARD_TITLE}
    outline
    action={
      isError ? (
        <Button variant="outlined" color="secondary" size="small" onClick={onRetry}>
          Retry
        </Button>
      ) : null
    }
    toolbar={toolbar}
    columns={COLUMNS}
    rowCount={locations.length}
    isPending={isPending}
    isError={isError}
    emptyMessage={
      hasSearch
        ? 'No locations match this search.'
        : 'This location user has access to no locations yet.'
    }
    errorMessage="Could not load locations."
    footer={footer}
  >
    {locations.map((location) => (
      <TableRow key={location.id}>
        <TableCell>
          {location.officeId ? (
            <RouteLink to={`/user/dentist/${location.officeId}`}>{location.name}</RouteLink>
          ) : (
            location.name
          )}
        </TableCell>
        <TableCell>{location.addressLabel}</TableCell>
        <TableCell>
          <CaptionedLabel label={location.accessLabel} caption={location.accessViaLabel} />
        </TableCell>
      </TableRow>
    ))}
  </TableCardFrame>
);

LocationsTable.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      officeId: PropTypes.string,
      addressLabel: PropTypes.string.isRequired,
      accessLabel: PropTypes.string.isRequired,
      accessViaLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  /** Picks the empty copy: "nothing matched" reads as a bug with no search typed. */
  hasSearch: PropTypes.bool.isRequired,
  onRetry: PropTypes.func.isRequired,
  /** Toolbar slot — the tab passes its search box. */
  toolbar: PropTypes.node.isRequired,
  /** Footer slot — the tab passes its pagination. */
  footer: PropTypes.node.isRequired,
};

export default LocationsTable;
